// Chromium and Firefox keep IndexedDB best-effort and may evict the site under
// disk pressure; Safari deletes script-written storage after seven days without
// a visit. The chronology's saves live in that storage, so the first Begin asks
// the browser, once, to mark the origin persistent instead of silently risking
// eviction while the title screen promises every choice is kept.

const askKey = 'dissidium:storage-persist:v1';

export const saveAtRiskLine = 'This browser may clear the save.';

export type Persistence = 'persistent' | 'granted' | 'refused' | 'unavailable';

type StorageManagerLike = { persisted?(): Promise<boolean>; persist?(): Promise<boolean> };
type NavigatorLike = { storage?: StorageManagerLike };
type WebStorage = Pick<Storage, 'getItem' | 'setItem'>;

const remembered = (web: WebStorage | undefined): 'granted' | 'refused' | 'never' => {
  try {
    const value = web?.getItem(askKey);
    return value === 'granted' || value === 'refused' ? value : 'never';
  } catch { return 'never'; } // A restricted session has not remembered an ask.
};

/** Has the browser already promised this origin durable storage? Never prompts. */
export async function isStoragePersistent(navigator: NavigatorLike = globalThis.navigator): Promise<boolean> {
  try { return await navigator.storage?.persisted?.() ?? false; } catch { return false; }
}

/**
 * Ask for persistent storage, once per browser. A remembered refusal is never
 * re-asked, so Firefox does not repeat its permission prompt for later lives;
 * a lapsed grant is, because the browser itself withdrew the promise.
 */
export async function requestPersistentStorage(
  navigator: NavigatorLike = globalThis.navigator,
  web: WebStorage | undefined = typeof localStorage === 'undefined' ? undefined : localStorage,
): Promise<Persistence> {
  const manager = navigator.storage;
  if (!manager?.persist) return 'unavailable';
  if (await isStoragePersistent(navigator)) return 'persistent';
  if (remembered(web) === 'refused') return 'refused';
  const granted = await manager.persist().catch(() => false);
  try { web?.setItem(askKey, granted ? 'granted' : 'refused'); } catch { /* The ask still happened this visit. */ }
  return granted ? 'granted' : 'refused';
}
